'use client';

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Filter, X, ChevronDown, ChevronUp } from 'lucide-react';

interface FilterOptions {
  countries: string[];
  durations: string[];
  months: string[];
  ageGroups: string[];
  themes: string[];
}

interface Filters {
  countries: string[];
  duration: string;
  durationMin: string;
  durationMax: string;
  months: string[];
  ages: string[];
  themes: string[];
}

interface CollapsibleJourneyFiltersProps {
  filters: Filters;
  setFilters: (filters: Filters) => void;
  filterOptions: FilterOptions;
  filteredCount: number;
  totalCount: number;
}

// Multi-select Checkbox Group Component
const MultiSelectCheckbox = ({ 
  options, 
  selected, 
  onChange, 
  placeholder 
}: {
  options: string[];
  selected: string[];
  onChange: (selected: string[]) => void;
  placeholder: string;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleOption = (option: string) => {
    if (selected.includes(option)) {
      onChange(selected.filter(item => item !== option));
    } else {
      onChange([...selected, option]);
    }
  };

  const displayText = selected.length === 0 
    ? placeholder 
    : selected.length === 1 
    ? selected[0] 
    : `${selected.length} selected`;

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500 text-sm bg-white text-left flex items-center justify-between hover:bg-gray-50 transition-colors"
      >
        <span className={selected.length === 0 ? 'text-gray-500' : 'text-gray-900'}>
          {displayText}
        </span>
        <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      
      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-auto">
          {options.length === 0 ? (
            <div className="px-3 py-2 text-sm text-gray-500">No options available</div>
          ) : (
            options.map((option) => (
              <label
                key={option}
                className="flex items-center px-3 py-2 hover:bg-gray-50 cursor-pointer transition-colors"
              >
                <input
                  type="checkbox"
                  checked={selected.includes(option)}
                  onChange={() => toggleOption(option)}
                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500 mr-2"
                />
                <span className="text-sm text-gray-900">{option}</span>
              </label>
            ))
          )}
        </div>
      )}
    </div>
  );
};

// Range Slider Component
const RangeSlider = ({ min, max, minValue, maxValue, onChange }: {
  min: number;
  max: number;
  minValue: number;
  maxValue: number;
  onChange: (min: number, max: number) => void;
}) => {
  const [localMin, setLocalMin] = useState(minValue);
  const [localMax, setLocalMax] = useState(maxValue);
  const [isDragging, setIsDragging] = useState<'min' | 'max' | null>(null);
  const sliderRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setLocalMin(minValue);
    setLocalMax(maxValue);
  }, [minValue, maxValue]);

  const getPercentage = (value: number) => ((value - min) / (max - min)) * 100;

  // Unified handler for both mouse and touch events
  const updateValue = useCallback((clientX: number) => {
    if (!isDragging || !sliderRef.current) return;

    const rect = sliderRef.current.getBoundingClientRect();
    const percent = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    const value = Math.round(min + percent * (max - min));

    if (isDragging === 'min') {
      const newMin = Math.max(min, Math.min(value, localMax - 1));
      setLocalMin(newMin);
      onChange(newMin, localMax);
    } else {
      const newMax = Math.min(max, Math.max(value, localMin + 1));
      setLocalMax(newMax);
      onChange(localMin, newMax);
    }
  }, [isDragging, localMin, localMax, min, max, onChange]);

  const handleStart = (type: 'min' | 'max') => {
    setIsDragging(type);
  };

  const handleEnd = () => {
    setIsDragging(null);
  };

  const handleMouseMove = useCallback((e: MouseEvent) => {
    updateValue(e.clientX);
  }, [updateValue]);

  const handleTouchMove = useCallback((e: TouchEvent) => {
    if (e.touches.length > 0) {
      updateValue(e.touches[0].clientX);
    }
  }, [updateValue]);

  useEffect(() => {
    if (isDragging) {
      const handleMove = (e: MouseEvent) => handleMouseMove(e);
      const handleTMove = (e: TouchEvent) => handleTouchMove(e);

      window.addEventListener('mousemove', handleMove);
      window.addEventListener('mouseup', handleEnd);
      window.addEventListener('touchmove', handleTMove);
      window.addEventListener('touchend', handleEnd);

      return () => {
        window.removeEventListener('mousemove', handleMove);
        window.removeEventListener('mouseup', handleEnd);
        window.removeEventListener('touchmove', handleTMove);
        window.removeEventListener('touchend', handleEnd);
      };
    }
  }, [isDragging, handleMouseMove, handleTouchMove]);

  return (
    <div className="w-full h-full flex items-center px-3 py-2">
      {/* Values display - compact inline */}
      <span className="text-[10px] text-gray-500 mr-2 whitespace-nowrap min-w-[32px]">{localMin}d</span>

      <div className="relative h-3 flex items-center flex-1" ref={sliderRef}>
        {/* Track background */}
        <div className="absolute left-0 right-0 h-0.5 bg-gray-300 rounded-full"></div>

        {/* Active range */}
        <div
          className="absolute h-0.5 bg-primary-600 rounded-full"
          style={{
            left: `${getPercentage(localMin)}%`,
            width: `${getPercentage(localMax) - getPercentage(localMin)}%`,
          }}
        ></div>

        {/* Min handle - larger touch target */}
        <div
          className="absolute w-11 h-11 -ml-5.5 cursor-grab active:cursor-grabbing touch-none flex items-center justify-center"
          style={{ left: `${getPercentage(localMin)}%`, top: '50%', transform: 'translateY(-50%)' }}
          onMouseDown={(e) => {
            e.preventDefault();
            handleStart('min');
          }}
          onTouchStart={(e) => {
            e.preventDefault();
            handleStart('min');
          }}
        >
          <div className="w-4 h-4 rounded-full bg-white shadow-md border-2 border-primary-600 hover:border-primary-700 active:scale-110 transition-all"></div>
        </div>

        {/* Max handle - larger touch target */}
        <div
          className="absolute w-11 h-11 -ml-5.5 cursor-grab active:cursor-grabbing touch-none flex items-center justify-center"
          style={{ left: `${getPercentage(localMax)}%`, top: '50%', transform: 'translateY(-50%)' }}
          onMouseDown={(e) => {
            e.preventDefault();
            handleStart('max');
          }}
          onTouchStart={(e) => {
            e.preventDefault();
            handleStart('max');
          }}
        >
          <div className="w-4 h-4 rounded-full bg-white shadow-md border-2 border-primary-600 hover:border-primary-700 active:scale-110 transition-all"></div>
        </div>
      </div>

      <span className="text-[10px] text-gray-500 ml-2 whitespace-nowrap min-w-[32px] text-right">{localMax}d</span>
    </div>
  );
};

export default function CollapsibleJourneyFilters({
  filters,
  setFilters,
  filterOptions,
  filteredCount,
  totalCount,
}: CollapsibleJourneyFiltersProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  // Clear all filters
  const clearFilters = () => {
    setFilters({
      countries: [],
      duration: 'all',
      durationMin: '1',
      durationMax: '30',
      months: [],
      ages: [],
      themes: []
    });
  };

  // Check if any filters are active
  const hasActiveFilters = 
    filters.countries.length > 0 ||
    filters.months.length > 0 ||
    filters.ages.length > 0 ||
    filters.themes.length > 0 ||
    filters.duration !== 'all';

  return (
    <div className="mb-4">
      {/* Mobile: Card with accordion */}
      <div className="lg:hidden bg-white rounded-lg border border-gray-200 shadow-sm">
        {/* Mobile Filter Header */}
        <div className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors rounded-lg">
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="flex items-center gap-3 flex-1"
          >
            <Filter className={`h-5 w-5 ${hasActiveFilters ? 'text-primary-600' : 'text-gray-600'}`} />
            <span className="font-semibold text-gray-900">Filters</span>
            <span className="text-sm text-gray-600">
              ({filteredCount} of {totalCount})
            </span>
          </button>
          <div className="flex items-center gap-2">
            {hasActiveFilters && (
              <button
                onClick={() => clearFilters()}
                className="text-sm text-primary-600 hover:text-primary-700 flex items-center px-3 py-1 rounded hover:bg-primary-50 transition-colors"
              >
                <X className="h-4 w-4 mr-1" />
                Clear All
              </button>
            )}
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="p-1"
              aria-label={isExpanded ? "Collapse filters" : "Expand filters"}
            >
              {isExpanded ? (
                <ChevronUp className="h-5 w-5 text-gray-600" />
              ) : (
                <ChevronDown className="h-5 w-5 text-gray-600" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Filter Content - Only show when expanded */}
        {isExpanded && (
          <div className="px-4 pb-4 border-t border-gray-200 space-y-4 pt-4">
            {/* Country Filter */}
            <div>
              <label className="sr-only">Country</label>
              <MultiSelectCheckbox
                options={filterOptions.countries}
                selected={filters.countries}
                onChange={(selected) => setFilters({ ...filters, countries: selected })}
                placeholder="All Countries"
              />
            </div>

            {/* Trip Length Filter */}
            <div className="border border-gray-300 rounded-lg bg-white">
              <label className="sr-only">Trip Length</label>
              <RangeSlider
                min={1}
                max={30}
                minValue={parseInt(filters.durationMin) || 1}
                maxValue={parseInt(filters.durationMax) || 30}
                onChange={(min, max) => {
                  setFilters({ ...filters, duration: 'range', durationMin: min.toString(), durationMax: max.toString() });
                }}
              />
            </div>

            {/* Departure Month Filter */}
            <div>
              <label className="sr-only">Departure Month</label>
              <MultiSelectCheckbox
                options={filterOptions.months}
                selected={filters.months}
                onChange={(selected) => setFilters({ ...filters, months: selected })}
                placeholder="All Months"
              />
            </div>

            {/* Age Group Filter */}
            <div>
              <label className="sr-only">Age Group</label>
              <MultiSelectCheckbox
                options={filterOptions.ageGroups}
                selected={filters.ages}
                onChange={(selected) => setFilters({ ...filters, ages: selected })}
                placeholder="All Ages"
              />
            </div>

            {/* Travel Theme Filter */}
            <div>
              <label className="sr-only">Travel Theme</label>
              <MultiSelectCheckbox
                options={filterOptions.themes}
                selected={filters.themes}
                onChange={(selected) => setFilters({ ...filters, themes: selected })}
                placeholder="All Themes"
              />
            </div>
          </div>
        )}
      </div>

      {/* Desktop: Single line, no card, no title, always visible */}
      <div className="hidden lg:block">
        {/* Desktop: Single row with all filters */}
        <div className="grid grid-cols-5 gap-4">
            {/* Country Filter */}
            <div>
              <label className="sr-only">Country</label>
              <MultiSelectCheckbox
                options={filterOptions.countries}
                selected={filters.countries}
                onChange={(selected) => setFilters({ ...filters, countries: selected })}
                placeholder="All Countries"
              />
            </div>

            {/* Trip Length Filter */}
            <div className="border border-gray-300 rounded-lg bg-white">
              <label className="sr-only">Trip Length</label>
              <RangeSlider
                min={1}
                max={30}
                minValue={parseInt(filters.durationMin) || 1}
                maxValue={parseInt(filters.durationMax) || 30}
                onChange={(min, max) => {
                  setFilters({ ...filters, duration: 'range', durationMin: min.toString(), durationMax: max.toString() });
                }}
              />
            </div>

            {/* Departure Month Filter */}
            <div>
              <label className="sr-only">Departure Month</label>
              <MultiSelectCheckbox
                options={filterOptions.months}
                selected={filters.months}
                onChange={(selected) => setFilters({ ...filters, months: selected })}
                placeholder="All Months"
              />
            </div>

            {/* Age Group Filter */}
            <div>
              <label className="sr-only">Age Group</label>
              <MultiSelectCheckbox
                options={filterOptions.ageGroups}
                selected={filters.ages}
                onChange={(selected) => setFilters({ ...filters, ages: selected })}
                placeholder="All Ages"
              />
              </div>

            {/* Travel Theme Filter */}
            <div>
              <label className="sr-only">Travel Theme</label>
              <MultiSelectCheckbox
                options={filterOptions.themes}
                selected={filters.themes}
                onChange={(selected) => setFilters({ ...filters, themes: selected })}
                placeholder="All Themes"
              />
            </div>
        </div>
      </div>
    </div>
  );
}
